import { createElement, useState } from "react";
import { AnnualWheelActivityStandardTemplateSaveModel, createNewModel } from "./AnnualWheelActivityStandardTemplate.types.ts";
import { IAnnualWheelActivityStandardTemplateDialogProps } from "./AnnualWheelActivityStandardTemplateDialog.tsx";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import { post, put } from "../../common/api/apiShared.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { getEnumValues } from "../../common/enumOperations.ts";
import { AnnualWheelActivityGeneralType } from "../../annualWheel/annualWheelDialog/AnnualWheelDialog.types.ts";
import { useTranslation } from "../../localization/useTranslation.ts";
import { RecurringIntervalType } from "../../annualWheel/AnnualWheel.types.ts";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAnnualWheelActivityStandardTemplateDialog(props: IAnnualWheelActivityStandardTemplateDialogProps) {
    const snackbar = useDotLegalSnackbar();

    const { translateString } = useTranslation();

    const [showError, setShowError] = useState(false);

    function initiateTemplateModel(): AnnualWheelActivityStandardTemplateSaveModel {
        return props.model
            ? {
                  id: props.model.id,
                  notificationPeriodInDays: props.model.notificationPeriodInDays,
                  recurringInterval: props.model.recurringInterval,
                  languages: props.model.languages,
                  complianceAreaIds: props.model.complianceAreas.map((c) => c.id),
                  generalType: props.model.generalType,
                  customId: props.model.customId,
                  imageUrl: props.model.imageUrl,
                  recurringIntervalType: props.model.recurringIntervalType,
              }
            : createNewModel();
    }

    const snackBar = useDotLegalSnackbar();
    const userContext = useUserContext();
    const [templateModel, setTemplateModel] = useState<AnnualWheelActivityStandardTemplateSaveModel>(initiateTemplateModel());
    const isEdit = !isNullOrWhitespace(templateModel.id);
    const { translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();

    const createTemplate = async () => {
        await createMutation.mutateAsync();
    };

    const updateTemplate = async () => {
        await updateMutation.mutateAsync();
    };

    const createMutation = useDotLegalMutation(create, {
        onSuccess: (response: any) => {
            const value = response.value();
            let updatedModel = { ...templateModel };
            updatedModel.id = value.id;

            const name = templateModel.languages.find((l) => l.twoLetterIsoLanguage === userContext.language)!.name;
            snackBar.show(createElement(Trans, { i18nKey: "dataCreated", values: { name: name } }));
            props.onSaved();
        },
    });

    const updateMutation = useDotLegalMutation(update, {
        onSuccess: () => {
            props.onSaved();
        },
    });

    function create() {
        return post<{}>("/AnnualWheelActivityStandardTemplates", templateModel);
    }

    function update() {
        return put<{}>(`/AnnualWheelActivityStandardTemplates/${templateModel.id}/`, templateModel);
    }

    // Null means a one-time template, so only a recurring template needs an interval.
    const isIntervalInvalid =
        templateModel.recurringInterval !== null && (templateModel.recurringInterval === undefined || templateModel.recurringInterval < 1);

    const onSave = () => {
        if (templateModel.languages.some((x) => isNullOrWhitespace(x.name))) {
            snackbar.show(translateString("titleRequiredInBothLanguages"), "warning");
            setShowError(true);
            return;
        }

        if (isIntervalInvalid) {
            setShowError(true);
            return;
        }

        if (isEdit) {
            updateTemplate();
        } else {
            createTemplate();
        }
    };

    const annualWheelActivityGeneralTypes = () => {
        return getEnumValues(AnnualWheelActivityGeneralType)
            .map((x) => {
                const name = translateAnnualWheelActivityGeneralTypeEnum(x.toString());
                return { name: name, id: x.toString() };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    const modelUpdater = {
        onNameChange: (lang: string, name: string | undefined) => {
            let updatedModel = { ...templateModel };
            updatedModel.languages.find((l) => l.twoLetterIsoLanguage === lang)!.name = name;
            setTemplateModel(updatedModel);
        },
        onDescriptionChange: (lang: string, description: string | undefined) => {
            let updatedModel = { ...templateModel };
            updatedModel.languages.find((l) => l.twoLetterIsoLanguage === lang)!.description = description;
            setTemplateModel(updatedModel);
        },
        onSubTaskChange: (lang: string, subTasks: Array<string>) => {
            let updatedModel = { ...templateModel };
            updatedModel.languages.find((l) => l.twoLetterIsoLanguage === lang)!.subTasks = subTasks;
            setTemplateModel(updatedModel);
        },
        onIntervalChange: (interval: string) => {
            let updatedModel = { ...templateModel };
            updatedModel.recurringInterval = isNullOrWhitespace(interval) ? undefined : Number(interval);
            setTemplateModel(updatedModel);
        },
        onNotificationPeriodInDaysChange: (notificationPeriodInDays: string) => {
            let updatedModel = { ...templateModel };
            updatedModel.notificationPeriodInDays = isNullOrWhitespace(notificationPeriodInDays) ? undefined : Number(notificationPeriodInDays);
            setTemplateModel(updatedModel);
        },
        onSingularActivityChange: (isSingleTimeActivity: boolean) => {
            let updatedModel = { ...templateModel };
            updatedModel.recurringInterval = isSingleTimeActivity ? null : (props.model?.recurringInterval ?? 12);
            updatedModel.recurringIntervalType = isSingleTimeActivity ? undefined : (props.model?.recurringIntervalType ?? RecurringIntervalType.Month);
            setTemplateModel(updatedModel);
        },

        onComplianceAreasChange: (complianceAreas: Array<string>) => {
            let updatedModel = { ...templateModel };
            updatedModel.complianceAreaIds = complianceAreas;
            setTemplateModel(updatedModel);
        },
        onGeneralTypeChange: (generalType?: string | null) => {
            const updatedModel = { ...templateModel };
            updatedModel.generalType = generalType ? Number(generalType) : undefined;
            setTemplateModel(updatedModel);
        },
        onCustomIdChange: (customId: string) => {
            const updatedModel = { ...templateModel };
            updatedModel.customId = isNullOrWhitespace(customId) ? undefined : customId;
            setTemplateModel(updatedModel);
        },
        onImageChange: (url: string | undefined) => {
            const updatedModel = { ...templateModel };
            updatedModel.imageUrl = url;
            setTemplateModel(updatedModel);
        },
        onRecurrenceTypeChange(type: string) {
            const updatedModel = { ...templateModel };
            updatedModel.recurringIntervalType = Number(type);
            setTemplateModel(updatedModel);
        },
    };

    return {
        onSave,
        isEdit,
        templateModel,
        modelUpdater,
        annualWheelActivityGeneralTypes,
        isSaving: createMutation.isLoading || updateMutation.isLoading,
        showError,
        isIntervalInvalid,
    };
}
